import type { RecipeIndexEntry } from './unifiedRecipeService';
import type { InventoryItem } from '../redux/inventorySlice';
import { isNonMealRecipe, isBreakfastRecipe } from '../utils/recipeClassification';
import { dietKindOf, type DietKind } from '../components/weekly/v2/dietKind';

/** "+1 fish this week": the kind the balance pick adds, as a literal key per kind. */
const BALANCE_REASON: Record<DietKind, string> = {
  fish: 'fanReasons.balanceFish',
  veg: 'fanReasons.balanceVeg',
  meat: 'fanReasons.balanceMeat',
};
import { hasIngredients } from '../utils/recipeContent';
import { DAY_PROFILE, rowOf, dayOf } from '../components/weekly/v2/constants';
import { narrowForSlot, type SlotFit } from './planRanking';
import { activeRows, pantryMatcherForNames, shelfMatcher, type PantryMatcher } from './pantryMatch';

/**
 * Shared builder for the "5 idées pour vous" fan of the Remplacer ce plat
 * panel (WeeklyDesktop + WeeklyMobile). Each of the 5 angles is honest —
 * fridge really scores against the inventory, balance against the week's
 * diet mix — and the pick is seeded-random among each angle's top
 * candidates so reopening the panel shows different ideas. An angle with no
 * real order of its own draws by chance across its whole pool (#864): sorting
 * those by time showed 18 recipes in 300 openings. The sheet's chips and its
 * « 5 nouvelles » live in `fanChips.ts`.
 */

export type FanAngleKey = 'fast' | 'fridge' | 'balance' | 'new' | 'fav';

/** Canonical display order — also the default set when `angleKeys` is omitted. */
export const ALL_FAN_ANGLES: FanAngleKey[] = ['fast', 'fridge', 'balance', 'new', 'fav'];

/**
 * The fields every angle actually reads. Declaring the input structurally (rather
 * than as a concrete entry type) lets both `RecipeIndexEntry` (weekly planner) and
 * `PersonalRecipeIndexEntry` (recipes screen) flow through without conversion —
 * they disagree on `servings`, which no angle looks at.
 */
export interface FanRecipe {
  id: string;
  title: string;
  readyInMinutes: number;
  handsOnMinutes?: number;
  dishTypes: string[];
  diets: string[];
  ingredients: string[];
  sourceRating?: number;
  translatedTitles?: { en?: string; fr?: string };
}

/**
 * The reason line in translatable form. `consequence` is the same sentence
 * pre-rendered in French — kept because the weekly planner reads it directly.
 * New surfaces should use `reasonKey` + `reasonParams` with `t()`.
 */
export interface FanReason {
  reasonKey: string;
  reasonParams?: Record<string, string | number>;
}

/**
 * One suggestion. Its reason line is `t(reasonKey, reasonParams)`, rendered by
 * the caller: this service used to hand back a pre-rendered French sentence as
 * well, and the planner showed it inside the English app.
 */
export interface FanSuggestion<T extends FanRecipe = RecipeIndexEntry> extends FanReason {
  angleKey: FanAngleKey;
  recipe: T;
}

/** The per-recipe user signals the angles need (subset of RecipeMetadata). */
export interface FanRecipeSignal {
  rating?: number;
  lastMade?: string;
  /** What each person at the table said (`fanChips`: a « no » is never a favourite). */
  verdicts?: Record<string, { value: string }>;
}

export interface FanSuggestionsParams<T extends FanRecipe = RecipeIndexEntry> {
  /**
   * v2 slot key (mi * 7 + di) — drives the day profile and breakfast handling.
   * Omit outside the weekly planner: no breakfast slot, and the `fast` angle
   * falls back to a 60 min ceiling instead of the day profile's `maxT`.
   */
  sk?: number;
  currentRecipeId?: string;
  allRecipes: T[];
  /** Recipe ids already placed anywhere in the current week. */
  placedRecipeIds?: string[];
  /** Active inventory items (selectActiveInventory). */
  inventory: InventoryItem[];
  /** Recipes that must never be suggested (confirmed allergens). Never widened. */
  exclude?: (recipe: T) => boolean;
  /** Per-recipe user signals, keyed by recipe id (recipeMetadataSlice). */
  metadataById: Record<string, FanRecipeSignal>;
  /** Randomization seed — a new one per panel opening gives fresh picks. */
  seed: number;
  /**
   * Which angles to build, in canonical display order. Defaults to all five.
   * `balance` is only meaningful with `placedRecipeIds` (a week to balance).
   */
  angleKeys?: FanAngleKey[];
  /**
   * Prefer recipes this returns true for — within each angle, before the seeded
   * pick; the rest are only reached when none qualifies. The recipes screen
   * passes "has a real photo" so a card never leads with the placeholder
   * drawing. The planner does not: which dish a slot gets must not depend on
   * whether it photographs well.
   */
  prefer?: (recipe: T) => boolean;
  /**
   * The dinner's day and the rest of its week (`slotFitFor`). When given, the
   * pool honours what the generators honour — the day's kind and time cap, and
   * no third dinner on one protein — giving way loosest first, exactly as
   * `narrowForSlot` does for a generated week. Dinner slots only: the schedule
   * says nothing about breakfast or lunch.
   */
  slotFit?: SlotFit;
  /**
   * False for a recipe the cook's taste rules out — a score at or below
   * `DISQUALIFYING_SCORE` (the opposite heat, a long dish on a quick night).
   * Such recipes go before the day and week are asked, as in the generators,
   * and come back only if nothing else is left.
   */
  viable?: (recipe: T) => boolean;
  /**
   * The recipes to draw from, already narrowed (`fanShelf`). Asking for five
   * more redraws without paying for the rules again. Replaces `allRecipes`'s
   * filtering; `allRecipes` still gives the week's mix and the current dish.
   */
  pool?: T[];
}

/**
 * A day-and-week tier is only accepted when it leaves at least this many
 * recipes, so a strict tier holding one dish cannot shrink the fan to a card.
 */
const SLOT_FIT_MIN_KEEP = 3;

/** How deep in each angle's ranking the seeded pick can reach. */
const TOP_K = 5;

/**
 * A stable pseudo-random rank per recipe for one seed. Sorted on, it shuffles
 * without a PRNG call per comparison. FNV-1a over seed + id.
 */
export function seededOrder(seed: number): (recipe: { id: string }) => number {
  const ranks = new Map<string, number>();
  return ({ id }) => {
    let rank = ranks.get(id);
    if (rank === undefined) {
      let h = (0x811c9dc5 ^ seed) >>> 0;
      for (let i = 0; i < id.length; i++) {
        h ^= id.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
      }
      rank = h >>> 0;
      ranks.set(id, rank);
    }
    return rank;
  };
}

// mulberry32 — tiny deterministic PRNG, good enough for UI shuffling
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A seed that is stable for a whole local day and unique per user, so "today's
 * idea" survives a reload and differs between household members — the opposite
 * of the planner's `Date.now()` seed, which is deliberately fresh per opening.
 * FNV-1a over `YYYY-MM-DD` + uid.
 */
export function dailySeed(localDateYMD: string, userId?: string): number {
  let h = 0x811c9dc5;
  const input = `${localDateYMD}|${userId ?? 'anon'}`;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Device-local `YYYY-MM-DD` — never UTC, or "today" would roll over at 20:00 in Montréal. */
export function localDateYMD(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * How many *distinct* pantry items a recipe uses.
 *
 * Counting matched lines instead inflates the number badly: scraped recipes
 * repeat an ingredient across several lines, and their `ingredients` array often
 * carries instruction sentences too — "Saumon Marry Me" scored 10 against a
 * 12-item pantry it actually shares 4 items with. The reason line is the whole
 * point of this angle, so it has to be a number the user can verify by looking
 * in their fridge.
 *
 * "Uses" is the recipe screen's answer, not a substring test (`pantryMatch.ts`):
 * a French shelf counts against an English recipe, and "lait" no longer counts
 * against "laitue". The matcher is kept per names array, so a caller that
 * holds one array for a whole ranking pays for the index once.
 */
const namesMatchers = new WeakMap<readonly string[], PantryMatcher<{ name: string }>>();

export function pantryHits(
  // Only the ingredient lines are read, so anything carrying them qualifies —
  // the week resolver scores the planner's entries through the same counter.
  recipe: { ingredients?: string[] },
  activeNames: readonly string[],
): number {
  if (!recipe.ingredients?.length || activeNames.length === 0) return 0;
  let matcher = namesMatchers.get(activeNames);
  if (!matcher) {
    matcher = pantryMatcherForNames(activeNames);
    namesMatchers.set(activeNames, matcher);
  }
  return matcher.itemsUsedBy(recipe).length;
}

/**
 * Per-call memo of the fridge count. Scoped to one buildFanSuggestions
 * invocation, so it never outlives the inventory it was computed against; the
 * fridge angle calls it from a sort comparator, O(n log n) times.
 *
 * The matcher itself is the shelf's (`shelfMatcher`): redux hands every
 * ranking the same inventory array until the pantry changes, and the matcher
 * keeps every line's answer, so only the first ranking after a pantry change
 * walks the library (#865 — it used to walk it on every call).
 */
function memoPantryHits(inventory: readonly InventoryItem[]) {
  const matcher = shelfMatcher(activeRows(inventory));
  const cache = new Map<string, number>();
  return (recipe: FanRecipe): number => {
    const hit = cache.get(recipe.id);
    if (hit !== undefined) return hit;
    const value = matcher.itemsUsedBy(recipe).length;
    cache.set(recipe.id, value);
    return value;
  };
}

/**
 * What a slot may be offered before the day and the week are asked: real meals
 * of its kind, none excluded, none already placed this week (a small library
 * can exhaust that, so it widens to placed recipes, never to non-meals), then
 * the cook's taste.
 */
export function eligibleFanRecipes<T extends FanRecipe = RecipeIndexEntry>(
  params: Omit<FanSuggestionsParams<T>, 'seed'>,
): T[] {
  const { sk, currentRecipeId, allRecipes, placedRecipeIds = [], exclude, viable } = params;
  const isBreakfastSlot = sk != null && rowOf(sk) === 0;
  const placed = new Set(placedRecipeIds);

  // A recipe with no ingredient is never a suggestion, in any slot: there is
  // nothing to cook or to buy (#745 follow-up — blank manual drafts).
  const isMeal = (r: T) => hasIngredients(r) && !isNonMealRecipe(r, { allowBreakfast: isBreakfastSlot });
  const baseFilter = isBreakfastSlot ? (r: T) => hasIngredients(r) && isBreakfastRecipe(r) : isMeal;
  const filterFn = exclude ? (r: T) => baseFilter(r) && !exclude(r) : baseFilter;

  // Strict pool: real meals not already placed this week. A small library can
  // exhaust it, so widen to placed recipes (except the slot's current one) —
  // the non-meal filter never widens.
  let pool = allRecipes.filter((r) => r.id !== currentRecipeId && !placed.has(r.id) && filterFn(r));
  if (pool.length < 5) {
    const inPool = new Set(pool.map((r) => r.id));
    const extras = allRecipes.filter(
      (r) => r.id !== currentRecipeId && !inPool.has(r.id) && filterFn(r),
    );
    pool = [...pool, ...extras];
  }
  // The same day and week rules a generated dinner obeys (#736 follow-up):
  // « Remplacer » offered meat on a vegetarian day, 90 minutes on a 30-minute
  // weeknight and a third chicken.
  if (viable) {
    const tasteful = pool.filter(viable);
    if (tasteful.length > 0) pool = tasteful;
  }
  return pool;
}

export function buildFanSuggestions<T extends FanRecipe = RecipeIndexEntry>(
  params: FanSuggestionsParams<T>,
): FanSuggestion<T>[] {
  const {
    sk,
    currentRecipeId,
    allRecipes,
    placedRecipeIds = [],
    inventory,
    metadataById,
    seed,
    angleKeys = ALL_FAN_ANGLES,
    prefer,
    slotFit,
  } = params;

  // Outside the weekly planner there is no slot: no breakfast row, and the
  // `fast` angle uses a neutral 60 min ceiling rather than a day profile.
  const hasSlot = sk != null;
  const maxFastTime = hasSlot ? Math.max(DAY_PROFILE[dayOf(sk)].maxT, 60) : 60;
  const wanted = new Set(angleKeys);

  let pool = params.pool ?? eligibleFanRecipes(params);
  if (slotFit && !params.pool) {
    pool = narrowForSlot(pool, (r) => r, slotFit.slot, slotFit.balance, { kind: true, minKeep: SLOT_FIT_MIN_KEEP });
  }
  if (pool.length === 0) return [];

  const hitsOf = memoPantryHits(inventory);
  const signalOf = (r: T): FanRecipeSignal => metadataById[r.id] ?? {};
  const wasTried = (r: T) => {
    const s = signalOf(r);
    return s.rating != null || s.lastMade != null;
  };
  const bestRating = (r: T) => signalOf(r).rating ?? r.sourceRating ?? 0;

  const curTime = allRecipes.find((r) => r.id === currentRecipeId)?.readyInMinutes ?? 0;

  // Diet mix of the week (placed recipes only) → least-represented kind
  const tally: Record<DietKind, number> = { meat: 0, fish: 0, veg: 0 };
  const byId = new Map(allRecipes.map((r) => [r.id, r]));
  for (const id of placedRecipeIds) {
    const r = byId.get(id);
    if (r) tally[dietKindOf(r)]++;
  }
  const leastDiet = (Object.entries(tally) as [DietKind, number][]).sort((a, b) => a[1] - b[1])[0][0];

  const rand = mulberry32(seed);
  const chosen = new Set<string>();

  // 0/undefined readyInMinutes means "unknown", not "instant" — rank last so
  // the fast angle can't surface a time-less recipe as the quickest option.
  const timeOf = (r: T) => (r.readyInMinutes && r.readyInMinutes > 0 ? r.readyInMinutes : 999);
  const byTime = (a: T, b: T) => timeOf(a) - timeOf(b);
  // Only « Plus rapide » is about speed. Sorted by time, the two angles below
  // drew from the same five quickest dishes as it did: 18 recipes in 300
  // openings out of 690 of 30 minutes or less, a radish sandwich for supper (#864).
  const order = seededOrder(seed);
  const byChance = (a: T, b: T) => order(a) - order(b);

  interface Angle {
    key: FanAngleKey;
    sorter: (a: T, b: T) => number;
    pred?: (r: T) => boolean;
    /** Bounds the random window to candidates as good as the best one. */
    window?: (best: T, r: T) => boolean;
  }

  const angles: Angle[] = ([
    {
      key: 'fast',
      sorter: byTime,
      pred: (r) => timeOf(r) <= maxFastTime,
    },
    {
      key: 'fridge',
      sorter: (a, b) => hitsOf(b) - hitsOf(a),
      pred: (r) => hitsOf(r) > 0,
    },
    {
      key: 'balance',
      sorter: byChance,
      pred: (r) => dietKindOf(r) === leastDiet,
    },
    {
      key: 'new',
      sorter: byChance,
      pred: (r) => !wasTried(r),
    },
    {
      key: 'fav',
      sorter: (a, b) => bestRating(b) - bestRating(a),
      // The angle claims "Valeur sûre" — only randomize among the top-rated tier
      window: (best, r) => bestRating(r) >= bestRating(best) - 0.01,
    },
  ] as Angle[]).filter((a) => wanted.has(a.key));

  // Rank the angle's candidates (predicate first, whole pool as fallback),
  // then let the seeded PRNG pick among the top K not yet chosen.
  const take = ({ sorter, pred, window }: Angle): T | null => {
    let candidates = pool.filter((r) => !chosen.has(r.id) && (!pred || pred(r))).sort(sorter);
    if (candidates.length === 0) {
      candidates = pool.filter((r) => !chosen.has(r.id)).sort(sorter);
    }
    if (candidates.length === 0) return null;
    if (prefer) {
      const preferred = candidates.filter(prefer);
      if (preferred.length > 0) candidates = preferred;
    }
    const windowed = window ? candidates.filter((r) => window(candidates[0], r)) : candidates;
    const pick = windowed[Math.floor(rand() * Math.min(TOP_K, windowed.length))];
    chosen.add(pick.id);
    return pick;
  };

  // Scarce angles pick first so e.g. the single recipe matching the fridge
  // isn't stolen by "fast" — display order stays canonical below.
  const qualifiedCount = (a: Angle) => (a.pred ? pool.filter(a.pred).length : pool.length);
  const picks = new Map<FanAngleKey, T | null>();
  [...angles]
    .sort((a, b) => qualifiedCount(a) - qualifiedCount(b))
    .forEach((angle) => picks.set(angle.key, take(angle)));

  const fast = picks.get('fast') ?? null;
  const fridge = picks.get('fridge') ?? null;
  const balance = picks.get('balance') ?? null;
  const neverTried = picks.get('new') ?? null;
  const fav = picks.get('fav') ?? null;

  const results: FanSuggestion<T>[] = [];
  const push = (angleKey: FanAngleKey, recipe: T | null, reason: FanReason) => {
    if (recipe) results.push({ angleKey, recipe, ...reason });
  };

  const savedMin = fast ? curTime - timeOf(fast) : 0;
  push(
    'fast',
    fast,
    fast && curTime > 0 && savedMin > 0
      ? { reasonKey: 'fanReasons.fastSaves', reasonParams: { count: savedMin } }
      : fast && fast.readyInMinutes > 0
        ? { reasonKey: 'fanReasons.fastOnly', reasonParams: { count: fast.readyInMinutes } }
        : { reasonKey: 'fanReasons.fastGeneric' },
  );

  const fridgeHits = fridge ? hitsOf(fridge) : 0;
  push(
    'fridge',
    fridge,
    fridgeHits > 0
      ? { reasonKey: 'fanReasons.fridgeHits', reasonParams: { count: fridgeHits } }
      : { reasonKey: 'fanReasons.fridgeGeneric' },
  );

  push('balance', balance, { reasonKey: balance ? BALANCE_REASON[dietKindOf(balance)] : 'fanReasons.balance' });

  push('new', neverTried, { reasonKey: 'fanReasons.new' });

  const favRating = fav ? (signalOf(fav).rating ?? fav.sourceRating) : undefined;
  push(
    'fav',
    fav,
    favRating
      ? { reasonKey: 'fanReasons.favRating', reasonParams: { rating: favRating.toFixed(1) } }
      : { reasonKey: 'fanReasons.favGeneric' },
  );

  return results;
}
